import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  toNumber,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";

export const TEMPERATURE_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

const celsiusToFahrenheit = (c: number): number => (c * 9) / 5 + 32;

export interface TemperaturePoint {
  ts: number;
  temp: number;
  speed: number;
  ignition: boolean;
  breach: boolean;
}

export interface TemperatureResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  points: TemperaturePoint[];
  min: number | null;
  max: number | null;
  avg: number | null;
  breaches: number;
  loading: boolean;
  error: string | null;
}

export interface TemperatureDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportTemperatureState {
  results: Record<string, TemperatureResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
  scale: "celsius" | "fahrenheit";
  highLimit: number;
  lowLimit: number;
}

interface ReportTemperatureActions {
  loadRange: (
    devices: TemperatureDevice[],
    from: string,
    to: string,
    opts: {
      scale: "celsius" | "fahrenheit";
      highLimit: number;
      lowLimit: number;
      tz?: string;
    },
  ) => Promise<void>;
  clear: () => void;
}

type ReportTemperatureStore = ReportTemperatureState & ReportTemperatureActions;

export const useReportTemperatureStore = create<ReportTemperatureStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,
  scale: "celsius",
  highLimit: 0,
  lowLimit: 0,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const scale = opts.scale || "celsius";
    const order = devices.map((d) => d.deviceId);

    const highDisplay = scale === "fahrenheit" ? celsiusToFahrenheit(opts.highLimit) : opts.highLimit;
    const lowDisplay = scale === "fahrenheit" ? celsiusToFahrenheit(opts.lowLimit) : opts.lowLimit;

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);

    const initial: Record<string, TemperatureResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: TEMPERATURE_COLORS[i % TEMPERATURE_COLORS.length],
        points: [],
        min: null,
        max: null,
        avg: null,
        breaches: 0,
        loading: true,
        error: null,
      };
    });

    set({
      results: initial,
      order,
      loading: true,
      from,
      to,
      tz: resolvedTz,
      scale,
      highLimit: highDisplay,
      lowLimit: lowDisplay,
    });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      const tempPoints: TemperaturePoint[] = [];
      let sum = 0;
      let min: number | null = null;
      let max: number | null = null;
      let breaches = 0;

      points.forEach((p) => {
        const rawTemp =
          toNumber(p.params?.temperature) ?? toNumber(p.params?.dallas_temperature);
        if (rawTemp == null) return;
        const temp = scale === "fahrenheit" ? celsiusToFahrenheit(rawTemp) : rawTemp;
        const breach = temp < lowDisplay || temp > highDisplay;
        if (breach) breaches++;
        sum += temp;
        min = min == null ? temp : Math.min(min, temp);
        max = max == null ? temp : Math.max(max, temp);
        tempPoints.push({
          ts: p.ts,
          temp,
          speed: toNumber(p.speed) ?? 0,
          ignition: p.ignition === true,
          breach,
        });
      });

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              points: tempPoints,
              min,
              max,
              avg: tempPoints.length > 0 ? sum / tempPoints.length : null,
              breaches,
              loading: false,
              error:
                tempPoints.length === 0 && failedChunks > 0
                  ? "No se pudo obtener la temperatura (servicio no disponible)."
                  : tempPoints.length === 0
                    ? "Sin datos de temperatura en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({
      results: {},
      order: [],
      loading: false,
      from: null,
      to: null,
      tz: null,
      scale: "celsius",
      highLimit: 0,
      lowLimit: 0,
    }),
}));
